import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';

/**
 * SnapAndSeekButton
 *
 * The GroovLens "camera" entry point. When the user is standing in a
 * record store, at a festival merch table, or thumbing through a friend's
 * vinyl shelf, they can tap this and shoot a photo of any physical
 * sleeve / CD case. The captured JPEG is uploaded to the SAME
 * `/api/scan/upload` endpoint the screenshot flow uses, so the AI vision
 * pipeline reads the album cover exactly as it would a Spotify screenshot,
 * enriches via iTunes → MusicBrainz → BPM cascade, and drops the resulting
 * card into the appropriate crate.
 *
 * Design token:
 *   The button uses the primary TEAL brand color, paired with the amber
 *   "Scan Screenshots" button as its peer feature. Two verbs of the same
 *   scan action, color-coded so users instantly parse which is which.
 *
 * Implementation notes:
 *   - Uses `<input type="file" accept="image/*" capture="environment">`.
 *     On mobile Safari + Chrome this opens the native camera app directly;
 *     on desktop it falls back to the standard file picker (still useful).
 *   - After a successful upload we hand off to /upload?batch=<id> so the
 *     existing batch-progress UI takes over — zero duplicated polling code.
 */
export default function SnapAndSeekButton({
  size,
  className = '',
  testid = 'snap-and-seek-button',
  labelDesktop = 'Snap & Seek',
  labelMobile = 'Seek',
}) {
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);

  const openCamera = () => {
    if (uploading) return;
    inputRef.current?.click();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    // Reset the input immediately so a subsequent tap re-triggers even
    // when the user picks the "same" filename (which browsers otherwise
    // suppress via onChange short-circuiting).
    if (inputRef.current) inputRef.current.value = '';
    if (!file) return;

    setUploading(true);
    const fd = new FormData();
    fd.append('files', file, file.name || 'snap.jpg');

    try {
      const { data } = await api.post('/scan/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      toast.success('Scanning your snap…');
      // Hand off to the Upload page which already polls the batch and
      // renders per-item progress + previews.
      navigate(`/upload?batch=${encodeURIComponent(data.batch_id)}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Snap failed — try again');
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <Button
        size={size}
        // Outlined-to-tinted-fill: at rest a teal-outlined ghost, on hover /
        // active / focus fills to a semi-transparent teal (35% opacity) with
        // white text — matching the "gentle fill" treatment used on the
        // Send-to-Spotify and Crate Video Wall buttons for consistent
        // hover rhythm across the app's headline actions.
        className={`rounded-xl border-2 border-[hsl(var(--primary))] !bg-transparent text-[hsl(var(--primary))] hover:!bg-[hsl(var(--primary))]/35 hover:text-white active:!bg-[hsl(var(--primary))]/35 active:text-white focus-visible:!bg-[hsl(var(--primary))]/35 focus-visible:text-white focus-visible:ring-[hsl(var(--primary))] hover:shadow-[0_0_24px_-8px_hsl(var(--primary))] transition-colors duration-150 ${className}`}
        onClick={openCamera}
        disabled={uploading}
        data-testid={testid}
        aria-label="Snap and Seek — take a photo of a physical album"
      >
        {uploading ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        ) : (
          <Camera className="w-4 h-4 mr-2" />
        )}
        <span className="sm:hidden">{labelMobile}</span>
        <span className="hidden sm:inline">{labelDesktop}</span>
      </Button>
      {/* Hidden file input — `capture="environment"` opens the REAR
          camera on mobile so users are aimed at the sleeve they're
          photographing, not their own face. */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFile}
        className="hidden"
        aria-hidden="true"
        data-testid="snap-and-seek-input"
      />
    </>
  );
}
